import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ArrowLeft, Send, Bot, CreditCard, FileText, Lightbulb, TrendingUp, CheckCircle, AlertCircle, MessageCircle } from 'lucide-react';
import IrisAvatar from './IrisAvatar';

interface ProjectSubmissionProps {
  onBack: () => void;
}

interface ProjectData {
  name: string;
  email: string;
  phone: string;
  company: string;
  domain: string;
  description: string;
  budget: string;
  timeline: string;
}

const ProjectSubmission: React.FC<ProjectSubmissionProps> = ({ onBack }) => {
  const [step, setStep] = useState(1);
  const [aiUsageCount, setAiUsageCount] = useState(0);
  const [projectData, setProjectData] = useState<ProjectData>({
    name: '',
    email: '',
    phone: '',
    company: '',
    domain: '',
    description: '',
    budget: '',
    timeline: ''
  });
  const [showPayment, setShowPayment] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const domains = [
    'E-commerce / Vente en ligne',
    'Santé / Médical',
    'Éducation / Formation',
    'Finance / Banque',
    'Immobilier',
    'Restauration / Hôtellerie',
    'Transport / Logistique',
    'Industrie / Manufacturing',
    'Services professionnels',
    'Technologie / IT',
    'Marketing / Communication',
    'Autre'
  ];

  const budgetRanges = [
    'Moins de 3 000 000 CFA',
    '3 000 000 - 10 000 000 CFA',
    '10 000 000 - 20 000 000 CFA',
    '20 000 000 - 35 000 000 CFA',
    '35 000 000 - 65 000 000 CFA',
    'Plus de 65 000 000 CFA'
  ];

  const timelineOptions = [
    'Urgent (moins de 1 mois)',
    'Court terme (1-3 mois)',
    'Moyen terme (3-6 mois)',
    'Long terme (6+ mois)',
    'Pas de contrainte'
  ];

  const handleInputChange = (field: keyof ProjectData, value: string) => {
    setProjectData(prev => ({ ...prev, [field]: value }));
  };

  const handleAIConsultation = (type: 'study' | 'advice') => {
    if (aiUsageCount >= 2) {
      setShowPayment(true);
      return;
    }
    
    setAiUsageCount(prev => prev + 1);
    // Simuler une consultation IA
    alert(`Consultation IA ${type === 'study' ? 'Étude' : 'Conseils'} lancée ! (${2 - aiUsageCount} utilisations restantes)`);
  };

  const handleSubmit = () => {
    setSubmitted(true);
    // Ici on enverrait les données au backend
    console.log('Projet soumis:', projectData);
  };

  const handleExpertStudy = () => {
    setShowPayment(true);
  };

  const getIrisMessage = (currentStep: number) => {
    const messages = {
      1: {
        title: "Bonjour ! Je suis Iris 👋",
        message: "Je vais vous accompagner dans la soumission de votre projet. Commençons par vos informations de contact pour que notre équipe puisse vous recontacter rapidement."
      },
      2: {
        title: "Parfait ! Parlons de votre projet 🚀",
        message: "Maintenant, décrivez-moi votre projet en détail. Plus vous serez précis, plus notre devis sera adapté à vos besoins spécifiques."
      },
      3: {
        title: "Excellent ! Choisissons l'assistance 🎯",
        message: "Votre projet m'intéresse ! Je peux vous aider avec mon IA ou vous pouvez opter pour une analyse approfondie par nos experts. Que préférez-vous ?"
      }
    };
    return messages[currentStep as keyof typeof messages];
  };

  if (submitted) {
    return (
      <div className="min-h-screen bg-ice flex items-center justify-center p-4">
        <motion.div
          initial={{ opacity: 0, scale: 0.8 }}
          animate={{ opacity: 1, scale: 1 }}
          className="bg-white rounded-2xl p-8 shadow-xl max-w-lg w-full"
        >
          {/* Header avec logo */}
          <div className="text-center mb-8">
            <h1 className="text-2xl md:text-3xl font-bold text-elegant text-white-gold mb-2">
              Iris Softech
            </h1>
            <p className="text-sm text-gray-600">Confirmation de soumission</p>
          </div>

          {/* Avatar Iris avec message de succès */}
          <div className="flex items-start space-x-4 mb-8">
            <div className="flex-shrink-0">
              <div className="relative">
                <div className="w-16 h-16 rounded-full overflow-hidden border-3 border-green-200 shadow-lg">
                  <img 
                    src="/irisavatar.jpg" 
                    alt="Iris IA" 
                    className="w-full h-full object-cover"
                  />
                </div>
                <div className="absolute -bottom-1 -right-1 w-6 h-6 bg-green-500 rounded-full border-2 border-white flex items-center justify-center">
                  <CheckCircle className="w-4 h-4 text-white" />
                </div>
              </div>
            </div>
            
            <div className="flex-1">
              <div className="bg-gradient-to-r from-green-50 to-blue-50 rounded-xl p-4 relative">
                <div className="absolute left-0 top-4 transform -translate-x-2">
                  <div className="w-4 h-4 bg-gradient-to-r from-green-50 to-blue-50 rotate-45 border-l border-b border-green-100"></div>
                </div>
                
                <h3 className="text-lg font-bold text-gray-900 mb-2">
                  🎉 Félicitations ! Votre projet a été soumis
                </h3>
                <p className="text-gray-700 leading-relaxed">
                  J'ai bien reçu toutes vos informations ! Notre équipe d'experts va analyser votre projet 
                  et vous contactera dans les <strong>24 heures</strong> avec un devis personnalisé.
                </p>
              </div>
            </div>
          </div>

          {/* Prochaines étapes */}
          <div className="bg-gray-50 rounded-xl p-6 mb-6">
            <h4 className="font-semibold text-gray-900 mb-4">📋 Prochaines étapes :</h4>
            <div className="space-y-3">
              <div className="flex items-center space-x-3">
                <div className="w-6 h-6 bg-blue-100 rounded-full flex items-center justify-center text-xs font-bold text-blue-600">1</div>
                <span className="text-sm text-gray-700">Analyse de votre projet par notre équipe</span>
              </div>
              <div className="flex items-center space-x-3">
                <div className="w-6 h-6 bg-blue-100 rounded-full flex items-center justify-center text-xs font-bold text-blue-600">2</div>
                <span className="text-sm text-gray-700">Préparation d'un devis personnalisé</span>
              </div>
              <div className="flex items-center space-x-3">
                <div className="w-6 h-6 bg-blue-100 rounded-full flex items-center justify-center text-xs font-bold text-blue-600">3</div>
                <span className="text-sm text-gray-700">Contact sous 24h pour discuter des détails</span>
              </div>
            </div>
          </div>

          <button
            onClick={onBack}
            className="btn-primary w-full"
          >
            Retour à l'accueil
          </button>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-ice">
      {/* Header avec logo Iris Softech */}
      <div className="bg-white shadow-sm border-b">
        <div className="container-max py-6">
          <div className="flex items-center justify-between">
            <button
              onClick={onBack}
              className="flex items-center space-x-2 text-gray-600 hover:text-gray-900 transition-colors"
            >
              <ArrowLeft className="w-5 h-5" />
              <span>Retour à l'accueil</span>
            </button>
            
            {/* Logo Iris Softech identique à l'accueil */}
            <div className="text-center">
              <h1 className="text-3xl md:text-4xl font-bold text-elegant text-white-gold">
                Iris Softech
              </h1>
              <p className="text-sm text-gray-600 mt-1">Soumission de Projet</p>
            </div>
            
            <div className="text-right">
              <div className="text-sm font-medium text-gray-700">Étape {step}/3</div>
              <div className="text-xs text-gray-500">Progression: {Math.round((step / 3) * 100)}%</div>
            </div>
          </div>
        </div>
      </div>

      <div className="container-max py-8">
        <div className="max-w-4xl mx-auto">
          
          {/* Message d'Iris avec avatar */}
          <div className="mb-8">
            <div className="bg-gradient-to-r from-blue-50 to-purple-50 rounded-2xl p-6 border border-blue-100">
              <div className="flex items-start space-x-4">
                <div className="flex-shrink-0">
                  <div className="relative">
                    <div className="w-16 h-16 rounded-full overflow-hidden border-3 border-white shadow-lg">
                      <img 
                        src="/irisavatar.jpg" 
                        alt="Iris IA" 
                        className="w-full h-full object-cover"
                      />
                    </div>
                    {/* Indicateur d'activité */}
                    <div className="absolute -bottom-1 -right-1 w-5 h-5 bg-green-500 rounded-full border-2 border-white flex items-center justify-center">
                      <MessageCircle className="w-3 h-3 text-white" />
                    </div>
                  </div>
                </div>
                
                <div className="flex-1">
                  <div className="bg-white rounded-xl p-4 shadow-sm relative">
                    {/* Flèche de dialogue */}
                    <div className="absolute left-0 top-4 transform -translate-x-2">
                      <div className="w-4 h-4 bg-white rotate-45 border-l border-b border-gray-100"></div>
                    </div>
                    
                    <h3 className="text-lg font-bold text-gray-900 mb-2">
                      {getIrisMessage(step).title}
                    </h3>
                    <p className="text-gray-700 leading-relaxed">
                      {getIrisMessage(step).message}
                    </p>
                  </div>
                </div>
              </div>
            </div>
            
            {/* Barre de progression élégante */}
            <div className="mt-6">
              <div className="flex items-center justify-between mb-3">
                <span className="text-sm font-medium text-gray-700">Progression du projet</span>
                <span className="text-sm text-blue-600 font-semibold">{Math.round((step / 3) * 100)}% complété</span>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-3 overflow-hidden">
                <motion.div 
                  className="bg-gradient-to-r from-blue-500 to-purple-500 h-3 rounded-full"
                  initial={{ width: 0 }}
                  animate={{ width: `${(step / 3) * 100}%` }}
                  transition={{ duration: 0.5, ease: "easeOut" }}
                />
              </div>
              
              {/* Étapes */}
              <div className="flex justify-between mt-2 text-xs text-gray-500">
                <span className={step >= 1 ? 'text-blue-600 font-medium' : ''}>Contact</span>
                <span className={step >= 2 ? 'text-blue-600 font-medium' : ''}>Projet</span>
                <span className={step >= 3 ? 'text-blue-600 font-medium' : ''}>Assistance</span>
              </div>
            </div>
          </div>

          <AnimatePresence mode="wait">
            {step === 1 && (
              <motion.div
                key="step1"
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -20 }}
                className="bg-white rounded-2xl p-8 shadow-lg"
              >
                <h2 className="text-3xl font-bold text-gray-900 mb-6">
                  Informations de contact
                </h2>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Nom complet *
                    </label>
                    <input
                      type="text"
                      value={projectData.name}
                      onChange={(e) => handleInputChange('name', e.target.value)}
                      className="input-field w-full"
                      placeholder="Votre nom et prénom"
                      required
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Email *
                    </label>
                    <input
                      type="email"
                      value={projectData.email}
                      onChange={(e) => handleInputChange('email', e.target.value)}
                      className="input-field w-full"
                      placeholder="votre@email.com"
                      required
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Téléphone
                    </label>
                    <input
                      type="tel"
                      value={projectData.phone}
                      onChange={(e) => handleInputChange('phone', e.target.value)}
                      className="input-field w-full"
                      placeholder="+33 6 12 34 56 78"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Entreprise
                    </label>
                    <input
                      type="text"
                      value={projectData.company}
                      onChange={(e) => handleInputChange('company', e.target.value)}
                      className="input-field w-full"
                      placeholder="Nom de votre entreprise"
                    />
                  </div>
                </div>
                
                <div className="flex justify-end mt-8">
                  <button
                    onClick={() => setStep(2)}
                    disabled={!projectData.name || !projectData.email}
                    className="btn-primary disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Suivant
                  </button>
                </div>
              </motion.div>
            )}

            {step === 2 && (
              <motion.div
                key="step2"
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -20 }}
                className="bg-white rounded-2xl p-8 shadow-lg"
              >
                <h2 className="text-3xl font-bold text-gray-900 mb-6">
                  Détails du projet
                </h2>
                
                <div className="space-y-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Domaine d'activité *
                    </label>
                    <select
                      value={projectData.domain}
                      onChange={(e) => handleInputChange('domain', e.target.value)}
                      className="input-field w-full"
                      required
                    >
                      <option value="">Sélectionnez votre domaine</option>
                      {domains.map((domain) => (
                        <option key={domain} value={domain}>{domain}</option>
                      ))}
                    </select>
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Description du projet *
                    </label>
                    <textarea
                      value={projectData.description}
                      onChange={(e) => handleInputChange('description', e.target.value)}
                      className="input-field w-full h-32 resize-none"
                      placeholder="Décrivez votre projet en détail : objectifs, fonctionnalités souhaitées, public cible..."
                      required
                    />
                  </div>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Budget estimé
                      </label>
                      <select
                        value={projectData.budget}
                        onChange={(e) => handleInputChange('budget', e.target.value)}
                        className="input-field w-full"
                      >
                        <option value="">Sélectionnez votre budget</option>
                        {budgetRanges.map((range) => (
                          <option key={range} value={range}>{range}</option>
                        ))}
                      </select>
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Délai souhaité
                      </label>
                      <select
                        value={projectData.timeline}
                        onChange={(e) => handleInputChange('timeline', e.target.value)}
                        className="input-field w-full"
                      >
                        <option value="">Sélectionnez un délai</option>
                        {timelineOptions.map((option) => (
                          <option key={option} value={option}>{option}</option>
                        ))}
                      </select>
                    </div>
                  </div>
                </div>
                
                <div className="flex justify-between mt-8">
                  <button
                    onClick={() => setStep(1)}
                    className="btn-secondary"
                  >
                    Précédent
                  </button>
                  <button
                    onClick={() => setStep(3)}
                    disabled={!projectData.domain || !projectData.description}
                    className="btn-primary disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Suivant
                  </button>
                </div>
              </motion.div>
            )}

            {step === 3 && (
              <motion.div
                key="step3"
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -20 }}
                className="space-y-8"
              >
                {/* Options d'assistance */}
                <div className="bg-white rounded-2xl p-8 shadow-lg">
                  <h2 className="text-3xl font-bold text-gray-900 mb-6">
                    Assistance pour votre projet
                  </h2>
                  
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                    {/* IA Gratuite - Étude */}
                    <div className="card hover-lift border-2 border-blue-200">
                      <div className="text-center">
                        <div className="w-16 h-16 bg-blue-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
                          <Bot className="w-8 h-8 text-blue-600" />
                        </div>
                        <h3 className="text-xl font-semibold text-gray-900 mb-3">
                          IA - Étude de projet
                        </h3>
                        <p className="text-gray-600 mb-4 text-sm">
                          Analyse automatique de votre projet avec recommandations techniques
                        </p>
                        <div className="text-2xl font-bold text-green-600 mb-4">
                          GRATUIT
                        </div>
                        <p className="text-xs text-gray-500 mb-4">
                          {2 - aiUsageCount} utilisations restantes
                        </p>
                        <button
                          onClick={() => handleAIConsultation('study')}
                          disabled={aiUsageCount >= 2}
                          className="btn-primary w-full text-sm disabled:opacity-50"
                        >
                          Lancer l'analyse IA
                        </button>
                      </div>
                    </div>

                    {/* IA Gratuite - Conseils */}
                    <div className="card hover-lift border-2 border-green-200">
                      <div className="text-center">
                        <div className="w-16 h-16 bg-green-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
                          <Lightbulb className="w-8 h-8 text-green-600" />
                        </div>
                        <h3 className="text-xl font-semibold text-gray-900 mb-3">
                          IA - Conseils marché
                        </h3>
                        <p className="text-gray-600 mb-4 text-sm">
                          Optimisation et positionnement par rapport au marché
                        </p>
                        <div className="text-2xl font-bold text-green-600 mb-4">
                          GRATUIT
                        </div>
                        <p className="text-xs text-gray-500 mb-4">
                          {2 - aiUsageCount} utilisations restantes
                        </p>
                        <button
                          onClick={() => handleAIConsultation('advice')}
                          disabled={aiUsageCount >= 2}
                          className="btn-primary w-full text-sm disabled:opacity-50"
                        >
                          Obtenir des conseils
                        </button>
                      </div>
                    </div>

                    {/* Étude Expert */}
                    <div className="card hover-lift border-2 border-purple-200">
                      <div className="text-center">
                        <div className="w-16 h-16 bg-purple-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
                          <TrendingUp className="w-8 h-8 text-purple-600" />
                        </div>
                        <h3 className="text-xl font-semibold text-gray-900 mb-3">
                          Étude par nos experts
                        </h3>
                        <p className="text-gray-600 mb-4 text-sm">
                          Analyse approfondie par notre équipe d'experts
                        </p>
                        <div className="text-2xl font-bold text-purple-600 mb-4">
                          5 000 FCFA
                        </div>
                        <p className="text-xs text-gray-500 mb-4">
                          Rapport détaillé sous 48h
                        </p>
                        <button
                          onClick={handleExpertStudy}
                          className="bg-purple-600 hover:bg-purple-700 text-white px-4 py-2 rounded-lg w-full text-sm transition-colors"
                        >
                          Commander l'étude
                        </button>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Soumission finale */}
                <div className="bg-white rounded-2xl p-8 shadow-lg">
                  <h3 className="text-2xl font-bold text-gray-900 mb-4">
                    Soumettre votre projet
                  </h3>
                  <p className="text-gray-600 mb-6">
                    Envoyez-nous votre projet pour recevoir un devis personnalisé gratuit sous 24h.
                  </p>
                  
                  <div className="flex justify-between">
                    <button
                      onClick={() => setStep(2)}
                      className="btn-secondary"
                    >
                      Précédent
                    </button>
                    <button
                      onClick={handleSubmit}
                      className="btn-primary flex items-center space-x-2"
                    >
                      <Send className="w-4 h-4" />
                      <span>Soumettre le projet</span>
                    </button>
                  </div>
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </div>

      {/* Modal de paiement avec Iris */}
      <AnimatePresence>
        {showPayment && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
          >
            <motion.div
              initial={{ scale: 0.8, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.8, opacity: 0 }}
              className="bg-white rounded-2xl p-8 max-w-lg w-full"
            >
              {/* Header avec logo */}
              <div className="text-center mb-6">
                <h1 className="text-xl font-bold text-elegant text-white-gold mb-2">
                  Iris Softech
                </h1>
                <p className="text-sm text-gray-600">Service Premium</p>
              </div>

              {/* Avatar Iris avec message */}
              <div className="flex items-start space-x-4 mb-6">
                <div className="flex-shrink-0">
                  <div className="relative">
                    <div className="w-14 h-14 rounded-full overflow-hidden border-3 border-orange-200 shadow-lg">
                      <img 
                        src="/irisavatar.jpg" 
                        alt="Iris IA" 
                        className="w-full h-full object-cover"
                      />
                    </div>
                    <div className="absolute -bottom-1 -right-1 w-5 h-5 bg-orange-500 rounded-full border-2 border-white flex items-center justify-center">
                      <AlertCircle className="w-3 h-3 text-white" />
                    </div>
                  </div>
                </div>
                
                <div className="flex-1">
                  <div className="bg-gradient-to-r from-orange-50 to-yellow-50 rounded-xl p-4 relative">
                    <div className="absolute left-0 top-4 transform -translate-x-2">
                      <div className="w-4 h-4 bg-gradient-to-r from-orange-50 to-yellow-50 rotate-45 border-l border-b border-orange-100"></div>
                    </div>
                    
                    <h3 className="text-lg font-bold text-gray-900 mb-2">
                      🎯 Passons au niveau supérieur !
                    </h3>
                    <p className="text-gray-700 leading-relaxed text-sm">
                      Vous avez utilisé vos <strong>2 consultations IA gratuites</strong>. 
                      Pour une analyse approfondie par nos experts humains avec rapport détaillé, 
                      c'est seulement <strong>5 000 FCFA</strong> !
                    </p>
                  </div>
                </div>
              </div>

              {/* Avantages de l'étude expert */}
              <div className="bg-gray-50 rounded-xl p-4 mb-6">
                <h4 className="font-semibold text-gray-900 mb-3">✨ Ce que vous obtenez :</h4>
                <div className="space-y-2 text-sm text-gray-700">
                  <div className="flex items-center space-x-2">
                    <CheckCircle className="w-4 h-4 text-green-500" />
                    <span>Analyse complète par nos experts</span>
                  </div>
                  <div className="flex items-center space-x-2">
                    <CheckCircle className="w-4 h-4 text-green-500" />
                    <span>Rapport détaillé sous 48h</span>
                  </div>
                  <div className="flex items-center space-x-2">
                    <CheckCircle className="w-4 h-4 text-green-500" />
                    <span>Recommandations personnalisées</span>
                  </div>
                  <div className="flex items-center space-x-2">
                    <CheckCircle className="w-4 h-4 text-green-500" />
                    <span>Estimation budgétaire précise</span>
                  </div>
                </div>
              </div>

              <div className="flex space-x-4">
                <button
                  onClick={() => setShowPayment(false)}
                  className="btn-secondary flex-1"
                >
                  Peut-être plus tard
                </button>
                <button
                  onClick={() => {
                    setShowPayment(false);
                    alert('Redirection vers le paiement sécurisé...');
                  }}
                  className="btn-primary flex-1 flex items-center justify-center space-x-2"
                >
                  <CreditCard className="w-4 h-4" />
                  <span>Payer 5 000 FCFA</span>
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default ProjectSubmission;
